// src/components/settings/SecurityTab.tsx
import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Save as SaveIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as VpnKeyIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { supabase } from "../../lib/supabase";
import { TOUCH_TARGET } from "../../theme/theme";

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: "error" | "warning" | "info" | "success";
}

const calculatePasswordStrength = (password: string): PasswordStrength => {
  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 2) return { score: 25, label: "Weak", color: "error" };
  if (score <= 3) return { score: 50, label: "Fair", color: "warning" };
  if (score <= 4) return { score: 75, label: "Good", color: "info" };
  return { score: 100, label: "Strong", color: "success" };
};

interface SecurityTabProps {
  onShowSnackbar: (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => void;
}

const SecurityTab: React.FC<SecurityTabProps> = ({ onShowSnackbar }) => {
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    label: "",
    color: "error",
  });

  useEffect(() => {
    if (passwordForm.newPassword) {
      setPasswordStrength(calculatePasswordStrength(passwordForm.newPassword));
    } else {
      setPasswordStrength({ score: 0, label: "", color: "error" });
    }
  }, [passwordForm.newPassword]);

  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.currentPassword) {
      onShowSnackbar("Please enter your current password", "error");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      onShowSnackbar("New password must be at least 8 characters", "error");
      return;
    }

    if (passwordStrength.score < 50) {
      onShowSnackbar("Please choose a stronger password", "error");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      onShowSnackbar("New passwords do not match", "error");
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      onShowSnackbar(
        "New password must be different from current password",
        "error"
      );
      return;
    }

    setPasswordLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.email) throw new Error("No user found");

      // Re-authenticate with current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordForm.currentPassword,
      });

      if (signInError) {
        throw new Error("Current password is incorrect");
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword,
      });

      if (updateError) throw updateError;

      // Clear form
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      onShowSnackbar("Password updated successfully!", "success");
    } catch (error: any) {
      console.error("Password change error:", error);
      onShowSnackbar(error.message || "Failed to change password", "error");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <VpnKeyIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update your password to keep your account secure. Use a strong,
            unique password that you don't use elsewhere.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                type={showPasswords.current ? "text" : "password"}
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    currentPassword: e.target.value,
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPasswords({
                            ...showPasswords,
                            current: !showPasswords.current,
                          })
                        }
                        edge="end"
                        sx={{ minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET }}
                      >
                        {showPasswords.current ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                type={showPasswords.new ? "text" : "password"}
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    newPassword: e.target.value,
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPasswords({
                            ...showPasswords,
                            new: !showPasswords.new,
                          })
                        }
                        edge="end"
                        sx={{ minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET }}
                      >
                        {showPasswords.new ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {passwordForm.newPassword && (
                <Box sx={{ mt: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength.score}
                      color={passwordStrength.color}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography
                      variant="caption"
                      color={`${passwordStrength.color}.main`}
                      fontWeight={600}
                    >
                      {passwordStrength.label}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Use 8+ characters with uppercase, lowercase, numbers, and
                    symbols
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPasswords.confirm ? "text" : "password"}
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    confirmPassword: e.target.value,
                  })
                }
                error={
                  passwordForm.confirmPassword !== "" &&
                  passwordForm.newPassword !== passwordForm.confirmPassword
                }
                helperText={
                  passwordForm.confirmPassword !== "" &&
                  passwordForm.newPassword !== passwordForm.confirmPassword
                    ? "Passwords do not match"
                    : ""
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPasswords({
                            ...showPasswords,
                            confirm: !showPasswords.confirm,
                          })
                        }
                        edge="end"
                        sx={{ minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET }}
                      >
                        {showPasswords.confirm ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={
                passwordLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleChangePassword}
              disabled={
                passwordLoading ||
                !passwordForm.currentPassword ||
                !passwordForm.newPassword ||
                !passwordForm.confirmPassword ||
                passwordForm.newPassword !== passwordForm.confirmPassword ||
                passwordStrength.score < 50
              }
            >
              Update Password
            </Button>
          </Box>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <SecurityIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Security Recommendations
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Email verified"
                secondary="Your email address has been verified"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Two-Factor Authentication"
                secondary="Add an extra layer of security to your account"
              />
              <ListItemSecondaryAction>
                <Chip label="Coming Soon" size="small" variant="outlined" />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SecurityTab;
